import {
  assertEvent,
  assign,
  fromPromise,
  raise,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {
  createEvent,
  getAvailability,
  getAvailabilityRange,
  noAvailability,
  updateEvent,
  createMoreAvailabilityEvent,
} from '../../services/schedularService';
import type {IGetAvailabilityQuery} from '../../types/dto/IGetAvailabilityQuery';
import type {ITimeSlots} from '../../types/ITimeSlots';

import type {
  IBookingMachineContext,
  IBookingMachineEvents,
  IBookingMachineInput,
  IBookingMachineOutput,
  SequenceDay,
} from './bookingMachine.types';
import {
  canNavigateNextWeek,
  hasSlotsInNormalRange,
  updateSequenceDays,
  getSelectedStartDateOfWeekFromSequenceDays,
  getSelectedTimeslots,
  generateSequenceDaysFromDayZeroDateChange,
  generateSequenceDaysFromAnyDayDateChange,
} from '../../utils/booking';
import type {IAdditionalQuestionAnswer} from '../../types/dto/IAdditionalQuestionAnswer';
import {
  addDays,
  formatISO,
  addTZDays,
  subTZDays,
  startOfTZDay,
  createTZDate,
} from '../../utils/date';
import type {AxiosError} from 'axios';
import {captureSentryMessage} from 'src/utils/sentry';
import type {IEvent} from '../../types/IEvent';


export const bookingMachine = setup({
  types: {
    context: {} as IBookingMachineContext,
    events: {} as IBookingMachineEvents,
    input: {} as IBookingMachineInput,
    output: {} as IBookingMachineOutput,
  },
  actors: {
    getAvailabilityRange: fromPromise(
      async ({
        input,
      }: {
        input: {templateId: string; eventId?: string; sessionId?: string};
      }) =>
        getAvailabilityRange(input.templateId, {
          event_id: input.eventId,
          session_id: input.sessionId,
        }),
    ),
    getAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          eventId?: string;
          sourceId?: string;
          startDate: string;
          endDate: string;
          sequenceDay?: number;
          sequencePrevDt?: string;
        };
      }) => {
        const params: IGetAvailabilityQuery = {
          viewer_timezone: input.timezone,
          event_id: input.eventId,
          source_id: input.sourceId,
          start_dt: input.startDate,
          end_dt: input.endDate,
          ...(input.sequenceDay && {
            sequence_day: input.sequenceDay,
          }),
          ...(input.sequencePrevDt && {sequence_prev_dt: input.sequencePrevDt}),
        };

        return getAvailability(input.templateId, params);
      },
    ),
    updateEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          date: string;
          startTime: string;
          timezone: string;
          attendee: string;
          firstAvailableDatetime: string;
          reason: string;
          aid: string;
        };
      }) =>
        updateEvent(input.eventId, {
          date: input.date,
          start_time: input.startTime,
          viewer_timezone: input.timezone,
          attendee: input.attendee,
          first_available_datetime: input.firstAvailableDatetime,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendNotification: fromPromise(
      ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          sessionId?: string;
          guestTimezone: string;
          sequenceDay: number;
        };
      }) =>
        noAvailability(input.templateId, {
          sequence_day: input.sequenceDay,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          session_id: input.sessionId,
          guest_timezone: input.guestTimezone,
        }),
    ),
    requestMoreAvailability: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          timezone: string;
          language: string;
          sessionId?: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
          source?: string;
        };
      }) => {
        return createMoreAvailabilityEvent({
          template_id: input.templateId,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          guest_timezone: input.timezone,
          guest_language: input.language,
          session_id: input.sessionId,
          source: input.source,
          additional_question_answers: input.additionalQuestionAnswers,
        });
      },
    ),
    createEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          templateId: string;
          timezone: string;
          date: string;
          startTime: string;
          guestName: string;
          guestEmail: string;
          guestPhone: string;
          firstAvailableDatetime: string;
          sessionId?: string;
          language: string;
          additionalQuestionAnswers: IAdditionalQuestionAnswer[];
          selectedTimeslots: Array<{
            day: number;
            date: string;
            start_time: string;
          }>;
        };
      }) => {
        return createEvent({
          template_id: input.templateId,
          viewer_timezone: input.timezone,
          date: input.date,
          start_time: input.startTime,
          guest_name: input.guestName,
          guest_email: input.guestEmail,
          guest_phone: input.guestPhone,
          first_available_datetime: input.firstAvailableDatetime,
          additional_question_answers: input.additionalQuestionAnswers,
          session_id: input.sessionId,
          guest_language: input.language,
          selected_timeslots: input.selectedTimeslots,
        });
      },
    ),
  },
  guards: {
    isTimeSlotUnavailable: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'UnavailableTimeSlot',
        'TimeSlotNotAvailable',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotRequestAlreadyExists: ({context}) => {
      const error = context.error as AxiosError<{errors: string[]}>;
      const errorKey = error?.response?.data?.errors?.[0];
      const unavailableTimeSlotErrorKeys = [
        'SlotRequestAlreadyExists',
      ] as const;
      return unavailableTimeSlotErrorKeys.includes(
        errorKey as typeof unavailableTimeSlotErrorKeys[number],
      );
    },
    isSlotsAvailableForCurrentWeek: ({context}) => {
      const {sequenceDays, currentSequenceNumber, availabilityRange} = context;

      if (!availabilityRange) {
        throw new Error('Availability range is not found');
      }

      const sequenceNumber = currentSequenceNumber ?? 0;
      const selectedDay = sequenceDays?.find(
        (day) => day.sequenceNumber === sequenceNumber,
      );
      const selectedStartDateOfWeek = selectedDay?.selectedStartDateOfWeek;
      const timeSlots = selectedDay?.timeSlots;

      if (!selectedStartDateOfWeek) {
        throw new Error(
          'Selected start date of week is not found for current day',
        );
      }

      if (!timeSlots) {
        return false;
      }

      const weekStart = formatISO(selectedStartDateOfWeek, {
        representation: 'date',
      });
      const weekEnd = formatISO(addDays(selectedStartDateOfWeek, 6), {
        representation: 'date',
      });

      const hasAvailableSlotsInCurrentWeek = Object.keys(
        timeSlots.free_time_slots || {},
      ).some((dateKey) => {
        return dateKey >= weekStart && dateKey <= weekEnd;
      });

      return hasAvailableSlotsInCurrentWeek;
    },
    isNewBooking: ({context}) => !context.eventDetails,
    isNoSlotsAvailableTillLastWeek: ({context}) => {
      const {
        sequenceDays,
        currentSequenceNumber,
        availabilityRange,
        timezone,
        eventTemplate,
      } = context;

      if (!availabilityRange) {
        throw new Error('Availability range is not found');
      }
      const selectedStartDateOfWeek =
        getSelectedStartDateOfWeekFromSequenceDays(
          sequenceDays,
          currentSequenceNumber ?? 0,
        );

      if (!selectedStartDateOfWeek) {
        throw new Error(
          'Selected start date of week is not found for current day',
        );
      }

      const currentSequenceDay = sequenceDays?.find(
        (day) => day.sequenceNumber === currentSequenceNumber,
      );
      const timeSlots = currentSequenceDay?.timeSlots;
      const hasSlotsAvailableTillCurrentWeek =
        Object.keys(timeSlots?.free_time_slots || {}).length > 0;
      const showExtendedAvailabilityByDefault =
        eventTemplate?.extended_future_limit?.policy === 'always';

      const hasSlotsInNormalRangeTillCurrentWeek = hasSlotsInNormalRange(
        timeSlots as ITimeSlots,
        availabilityRange.end_at,
        timezone,
      );

      const canNavigateToNextWeek = canNavigateNextWeek({
        timezone,
        selectedStartDateOfWeek,
        availabilityRange,
        hasNoSlotsInNormalRange: !hasSlotsInNormalRangeTillCurrentWeek,
        currentSequenceDay: currentSequenceNumber,
        showExtendedAvailabilityByDefault,
      });

      /**
       * If there are no slots available till current week and also we cannot navigate to the next week. that means user is on last week without any slots.
       */
      return (
        Boolean(!canNavigateToNextWeek) &&
        Boolean(!hasSlotsAvailableTillCurrentWeek)
      );
    },
    isDayZero: ({context}) => {
      return (context.currentSequenceNumber ?? 0) === 0;
    },
    isRescheduleOrCancellationNotAllowed: ({
      context,
    }: {
      context: IBookingMachineContext;
    }) => {
      return (
        Array.isArray(context.eventDetails) &&
        context.eventTemplate?.distribution_rule === 'sequential_interview'
      );
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCMD2qDWBLAdlAxADYCGeArsTAHRkAOExALmANoAMAuoqLarFoyyoc3EAA9EAWgCMAFgBsAdioAmAJwBWDfLZaAzPJUaAHABoQAT0TTpKvVQ1sVbWWtnTF0x9IC+P82iYuFBUAMYAFmChGABKcBGQZIRgAPIATgDCpKFghCSCwgByqIwAgnmoAO6Q+OxcSCC8-AUiDRIIkoqK8lTGakpsxsYKsnom5lYIxvL2anYzzrZ6xrZ+AejYeGGR0XGwCRBJqZnZuflCOMVlFdUQtdL1PHwCF6LtkstsvRoqznpecjYOnkE0Q01m8z0izsKzWIECmxCxAAbsQsCRkOiBBYYqQYAAZVDECDBfAQYRgKi4ZGYSkI4JUFFojFYxg4vFgQnE4IIamoUJMC51OqiJovYRvKSLKh6XTGDRqRRA+ZmSyIfo9NTGTxsDzTExauH0rZM9HETGEbG4vCcokkvD4MBpNKoNJUWj5ABmroAtlRjUjUWaLVaOVz7VBeTgaQKWsLOKLni1JQgVLIVL0dLq2Gw9LKTIo9KCEJoM25dWnZYWVPJfP54RsGQxmAAVLA+sAABSw0Sd+FgjGIaUYABEmKlPQB1MBgDDbDkihpi5NtaxfDQ2ZZeJXy4xOYs2OZUUbGMYbn5atR6I2NrbNsBtjvd3tpMnj+c2xdPZqvVcdKH2DWbD9J4sqKl4GgHnM9hAho-x6JoihqDm171gGVD3o+XY9hgfbVLOVA4GAYiMF+jRJr+oDvEMXw1nBwFOLYrgqAeKheMesiyI4hhKkq8jyDeQR3uOWHPrhr74XOtBpGAyJkculHiFINEysh-wmKe3gsWqCDSAYsjHooKjTEo8haLI0yCYiGEie22Evm+FiEcRpEJkuFESn+Mi1jKCHGfIe4KshiiseoVCDN4RmyEqchWU2tlPjhfYME50myfJHmtFRUgbtIVBqSoijRa4XR6CFOl6dIaiqNFtYVlV6ayHFwmtnZYl9jJACOZBwIwACyrpgKUQYspabIAGK+lQUCoBlP6edlCBsMWbDNSEmFtUlbqmqN2LhqS5JEVS0a0v6t7rQl9niYyI3mqyFj7XgUYxoKwjxo85HzVlSlTPYJjSEoGiKNoch6aqkwA2VVBdExG6LLqAloedNmtYlL43cyd1jQ9dqkk6Lpuh6TDemkfroRtaPXTtWN7bjT18rGQqcHN4rfe0GjRaoIGlsxwNqAeeZ5aZAOKmV6Z6WtKMPpt6PUyGbLhjULMrotYz5U4OaKCsgxjNqUEWeF7iaECLjyoVksU1dToY8G92K3cLAPImX0piYZbGM4tZQgFZXlRDbjGIb0jymZAGDGoFuXe1223fLOPEkrKgfQpC0-fxXzAXIoz9Lovwgjpix5VoZnDNITiXookeo1bMeY3HACizquvgMmMGkFjK4p7SyMBVDyJxurpkoxlVqxhiqJViwlwYKhV9LlPW3L90AMqECUsD4IIHawGvjBhMInpYKTnep+0fT5UYXGOLq2jDODiBsfI1UGGowfIRZaZz6JW1UFvcC7xkA+R8fSvRwJvOyO8Sj7xwIfY+blvysxTFVZQXE9wuABt0LM99UxcRQY-HMWo9zDC-jLa6f9IGMEATA4BoD8ABkmqTaas14GfUQV5ZwDh+4GD0nDQh8pixGCUFQAGypAZFTUG4EhC83TkIAUA0mtCZL7EiIcZIcRiCwGEAwv0M0T5s0QMsaqSojBFVsECTw2DBF5WmHpfo7gEJ9CkTXX+EC5HUIUS0OhxBoh6JTDIZQ5iTC608IsWQAjKwyhrKeRUmce6I3WEJC61do4uO3m42BIDPFdR6gOAaMlhp13utophvivK2C0H3cpegB56V+GEguHMjE90CsDLQ6gnEpKXtjK4WBPQWHwDgEovSLAAAk+B710SwlO+jdLVI0PlAshYLLaiUFBHQnCjJQh7sZGwTUkaJKlt-WWsd7o9L6Y5d0Mk5JTMyqIf2HNwpA2MvRUYbFVTtFqfYMYwMuhzAUD8YwksumhhtI3AmLcwBtw7jcl2f4zHCMaX0X4eZZTaUmPKax9jgY1n4p4SWgzBDnOQN4jApTQD3J6D8LwvxNwuAVOYdoaYArfG1D8H4UJgKV32dZAlwyqAkx9AMoZfSqCwDIMgH0AgyU-Q8AbdwFl-n8VGMDARch7Dak0LfKlhZ8XCqcmKiVAhBAOkOpSPkuEzoHN5SKg1krGDGsjAzUB71nZsMWmxJU4Vh4SMLAWYYAjqk9HcLsrwzTkG6sJfq8VdqHWOiboTL0U10LWqjYa+1PInVxmZjCt1MqJHVUVBzeUugFRoofvMaGakTAD09hGvl+Nm4ptFdGqVOaVY-UWMoMYThPgew5k-VVoxehIS4nIfyBhJb0N9HQ86xTiU+LbV3KQczehmyMrfMqRgBGoOPEMWwXQPZuC6JO2dU0BX4AFc2tN0r3jAlUMXIwbAuhwXqZMYymhoYapBqeDw8SGwHKnYw21RqDoUmOjSC16FAN+mA+m+mJ1GZvWzcnW5XkgY9HUI4apB6cwrGLHmIEqhcxKCKoMdwlluUMmg1emNeN43ukTYwqDp6gMtrg46hDzrkOuvbe8GsBkmX6lcAFLw+dJj-EIzYOQcw6pKpPYk4pDbXw3oMQZV+YwJHB3ZUMV9D9+h5U5d4U8xj+7ycRIp+N9wUOwsWjITix44Il1rIWIyesC76fCoW3UxmnCmco1sajrd27LzYxeqasGVMdAsj0GiJsxgAw5pxARmhA4eF0HKaseYzPBGKWQHActkhYVXiUCFnpIURAKbbbGJTF2nykCLL1dEP5bKGGJh+O7ln7q1uoCyktsm9TyUNE52NtEQu6gNwalXdoTSTSSyLMgtDVUMI4dDAUokCMadDZpuhWkXj62AcbuTJvDexMU89l6Iu1Zmd5Myqgyr91cILJYyX0zhQxT7CyoMND7cO-1Y7hSRvhbY7G014HTroX60d-JJ2ZusbTQ656-IuMcHmx4AGRG0xakaQqCy+GazKFc7+pYfy-0Q4Ozkv70OAenaB-DujBMGPEyTcjSHlOhvU9hzB4HGbONZpR1dvxSxKXdCBhuXFHsiw6Qk0t7QcxdRyA1T9ing2ps0051QJTtQBdeXzURmYywgYViSwXeURin4Fni0DPZCTrKs5VzDiwFnwWO2s7m94NgkJ3a4ghCxjgjICOmAZIhnEVgwy1kribVOqs08YUFiwIW01hbh3a1H6Yvgie0FWEXch8ODy5ti-uvwgRcUlhQuIv3ygyWJBYeuYgsADg3jJMrjAKsO5q673jUgfUylPEMRLyx1DSA2+xEemr12oRtwyJRBwjijenyoo46jNE4DnXN7Xi0fbCNqv8Qw76qobcLKoQsHtjLpkLBHfzIR5+JGSMU6-qiwBL60dO+-i+wAaOEDQeg45IvTEDlrJCbQHtNlAPNiVQUuJ9VlVYS-KgV-W-KaOAx-d-ZfUbWRKBUIeRH0SLDmDMfuEWTSEjVZdzbQGUboeUZYMqXMQFGAxAu-eIBfeA0mBPWjE1MDc1OkFnegm-MAOg5Rbg7RZgkDeDF6PnbAn4T9XBcydSAGfDHfI-FYMqLULWG+fbPgh-XgmfRgn0QQ9jONBnImRgS9MnNQ2fBArg9Q30HQhHTNJmfnDvJdKYJwcKLoOwEqEOBCXPHyS8GifjGxCff9W3cw0wxhTXSLMdewUxUNJCNLf3YgwOQwfiOYJCQRQwVQzQngqaUIp2dyGzGVHZaGfyPcaTfXMtVMK8DDHuRibUBiTQNIhgjIxhPLAreeMAYrRgBhPYdI0rcrcIVXOODooI5IdvHjBwvSboFlGeYyYYUYANHMPuL2IqJlYCOo-gswqFTo+ogQ0LOPDY1Ypg7nPASLIyL4IqEqK8XZYuZLTwcKXQTie7DBPzesQZCAOAUQAMEYurDoYOXUBZIwcg-iSg0oyQbUBwHMHMNMPtUNfw9CBIXYQY44LIHAHIPIUBK4coNeW4D467WwQqLbQNGYJ+HQUomxPuQsLMbcfXC-SfLYRAxE5E84IoEodEqoSALEvxYGPKWxPOcxYYSCHSfoewfdfuOXPdJwIFB3a0AkOmKANk9hJ9Y8AwZYFYIkzwGYYsXk-KFYBXAGFZa3AI+KZJLaWU2zRbdWKEIqcRUqP2awMuNTAfboQhX4IyDpH+YFBWaU40n6T1IEHQLWIeTiCRSXCGTdBzapRUU8MqYhGAy2TpB3e2T0s+eZeUXKfuLoLWPobBDwLUY8dOM-QqQvag6kpJFo2MjnGveNBMxAbQATMuYydMqJHPCqGsDMNiDlEtExKMosw5UhReB3No+AHIt3NcaxC8ZbItHZa01MHbYRYOHZNiT5PU8mKOV0h3euH0WgNkSshAfuE4xbc-RYwwARYCf-YCNiJwJUCRTiF09GNAyhTA0BLcoqSpdQMyTwLM7USxMM-KHQNlUTH4Gsa8qmB3M5SYBBTvXSFwaqUdc0knAFKCB5HbZ5WUV5YOcUssyUsAMFV0Lcx9ZwjVDwboAwJCdUjcXoLFH4fiJQOsLsgMELUIHIWAAcsC0YlwBwSgixcxOwelKXVFfKWUMyQYWwGwb7GAlNOihipi1hcCtibMrihiQPbQGsbdGYdWaYYyHZKEOwOtPpLcgGHySAkdA9IYTMzQSlNiO4gOEeQs-UrYJtAVXSrWPKIeDkmlf6Pkt9FwAyRif82lLiKkmykIJtWDB1By3uAyo9dMoYcJdwfKBLcESA-y5NPVDXCswc6SuwPKbtcsHtZwRUQddVEdDcdMcEUnZGaDLcyQJQAyBLQwFwdQXMLdAuVzXoCRLilLZ5bLPAM7X0CqoGZQPSEPOQH5dMfmE3dHBCc49wOwFwLlGiljLnOnPACqhI4RMyWqkahq9ygxJ9L4focg1+MXIqTqqAJ3bCtKhwmQTMYGdOLHNMC0rahAf4DwGUYCUyboW0rSmAwLSFYLNjCquQ6YJCUyoyLULwZLEwMi7FKqAk50r6+amgfLWOQrOyNorczmOxBCUYEqCaoMh+DVC+YCCRASpCJ9CPKHdnaPTnCqtHQCe7AMp7OwARddGUOCf4NPRULoUqg5O3f7Smx3M9Hq86z47yBQTUomuYK8LQB6uc74PMaCIqDmMmtnPoopWnFgmUoW7EgS1QHQWUfSUsfiXPCRBwXBPyMxAwLmwI37e3Ms06tICq03N7BqsyNwV+fufDUGAonMfiPoJ9TQRczg623m6bfm2PH6+PP6zWwXGEL1AKK8JwaYj2XPOQfPCi9MT2AOg5MvcnXqSvd-CAGvOvBvamlCaGJUI2RwR+NrVMQwQOVlQqFwoGD2FYiw0mNGz1dMDcV+NStMTiaWzZTMJu0sP03rGg+EjQ+op-FfQW5iz4runW2sNLPWrQSxLUmUJzB0oCWagK2A8eswkwrQqw4ILcjcQOTuqqYOXfTiXTR63QIxAGLiHuSu6pFu4Iv0JTXSwYfquYOQYeEJGYqXHWCeHc2sX4FYRKzgg+hov0JopGlotogYqB3SgfPuf4brTicIgPNWTPMB6LQKV+rQ3e9Yve-YtNT+34cA98vMHFXHdzceUGrWd+OcpWm2vm8SuASS6ZQXYGWYNlf4mYYxWQo8AGQqMYGsLkvwPwIAA */
  context: ({input}) => input,
  id: 'booking',
  initial: 'checkRescheduleOrCancellationNotAllowed',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => {
          return event.language;
        },
      }),
    },
  },
  states: {
    checkRescheduleOrCancellationNotAllowed: {
      always: [
        {
          target: 'rescheduleCancellationNotAllowed',
          guard: 'isRescheduleOrCancellationNotAllowed',
        },
        {
          target: 'availabilityRangeLoading',
        },
      ],
    },

    rescheduleCancellationNotAllowed: {},

    availabilityRangeLoading: {
      invoke: {
        id: 'booking.availabilityRangeLoading:invocation[0]',
        input: ({context: {eventDetails, session, eventTemplate}}) => {
          const templateId =
            eventTemplate?.id ||
            (eventDetails as IEvent)?.scheduled_from_template_id;
          if (templateId) {
            return {
              templateId,
              eventId: (eventDetails as IEvent)?.id,
              sessionId: session?.id,
            };
          }
          throw new Error('Template id not found');
        },
        onDone: {
          target: 'dateTimePicker',
          actions: assign({
            availabilityRange: ({event}) => event.output,
            currentSequenceNumber: 0,
            sequenceDays: ({event, context}) => {
              const startAt = event.output.start_at;
              if (!startAt || isNaN(startAt)) {
                captureSentryMessage(
                  'Invalid start_at from Availability Range API',
                  {
                    level: 'error',
                    tags: {
                      product: 'scheduler',
                    },
                    extra: {
                      apiParams: {
                        templateId: context.eventTemplate,
                        sessionId: context.session?.id,
                        eventId: (context.eventDetails as IEvent)?.id,
                      },
                      timezone: context.timezone,
                      rangeApiResponse: event.output,
                      eventDetails: context.eventDetails,
                      eventTemplate: context.eventTemplate,
                    },
                  },
                );
              }
              const startDayOfWeekFromRangeApi = createTZDate(
                startAt * 1000,
                context.timezone,
              );
              return [
                {
                  sequenceNumber: 0,
                  selectedStartDateOfWeek: startDayOfWeekFromRangeApi,
                  selectedDate: undefined,
                  selectedTimeSlot: undefined,
                },
              ];
            },
          }),
        },
        onError: {
          target: 'availabilityRangeError',
          actions: assign({
            error: ({event}) => event.error,
          }),
        },
        src: 'getAvailabilityRange',
      },
    },

    dateTimePicker: {
      initial: 'availabilityLoading',
      on: {
        'startDateOfWeek.change': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            sequenceDays: ({context, event}): SequenceDay[] => {
              // Preserve existing timeslots when changing week for sequence 0
              const existingDayZero = context.sequenceDays?.[0];
              return [
                {
                  sequenceNumber: 0,
                  selectedStartDateOfWeek: event.date,
                  selectedDate: existingDayZero?.selectedDate,
                  selectedTimeSlot: undefined,
                  timeSlots: existingDayZero?.timeSlots,
                },
              ];
            },
          }),
        },
        'date.change': {
          actions: assign({
            sequenceDays: ({context, event}): SequenceDay[] => {
              const {
                sequenceDays = [],
                eventTemplate,
                timezone,
                currentSequenceNumber = 0,
              } = context;
              const changedDaySequenceNumber = currentSequenceNumber;

              const generatedSequenceDays =
                generateSequenceDaysFromAnyDayDateChange({
                  changedDaySequenceNumber,
                  newDate: event.date,
                  existingSequenceDays: sequenceDays,
                  sequence: eventTemplate?.metadata?.sequence,
                  timezone,
                });

              return generatedSequenceDays;
            },
          }),
          target: 'dateTimePicker.availabilitySlots',
        },
        'week.next': {
          guard: 'isDayZero',
          actions: raise(
            ({context: {sequenceDays, currentSequenceNumber, timezone}}) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedStartDateOfWeek =
                getSelectedStartDateOfWeekFromSequenceDays(
                  sequenceDays,
                  sequenceNumber,
                );
              if (!selectedStartDateOfWeek) {
                throw new Error('Selected start date of week is not found');
              }
              return {
                type: 'startDateOfWeek.change',
                date: addTZDays(selectedStartDateOfWeek, 7, timezone),
              };
            },
          ),
        },
        'week.prev': {
          guard: 'isDayZero',
          actions: raise(
            ({context: {sequenceDays, currentSequenceNumber, timezone}}) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedStartDateOfWeek =
                getSelectedStartDateOfWeekFromSequenceDays(
                  sequenceDays,
                  sequenceNumber,
                );
              if (!selectedStartDateOfWeek) {
                throw new Error('Selected start date of week is not found');
              }
              return {
                type: 'startDateOfWeek.change',
                date: subTZDays(selectedStartDateOfWeek, 7, timezone),
              };
            },
          ),
        },
        'day.next': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) + 1,
          }),
        },
        'day.prev': {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) - 1,
          }),
        },

        'requestMoreAvailabilityForm.go': {
          target: '#booking.requestMoreAvailabilityForm',
        },
      },
      states: {
        availabilityLoading: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                eventTemplate,
                timezone,
                session,
                availabilityRange,
                sequenceDays,
                currentSequenceNumber,
              },
            }) => {
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;
              try {
                if (!templateId || !availabilityRange) {
                  throw new Error('Template id or availabilityRange not found');
                }

                const sequenceNumber = currentSequenceNumber ?? 0;
                const selectedStartDateOfWeek =
                  getSelectedStartDateOfWeekFromSequenceDays(
                    sequenceDays,
                    sequenceNumber,
                  );

                if (!selectedStartDateOfWeek) {
                  throw new Error(
                    'Could not determine selectedStartDateOfWeek for current day',
                  );
                }

                const currentDate = selectedStartDateOfWeek;
                const addedDate = addTZDays(currentDate, 7, timezone);
                const startDate = formatISO(
                  startOfTZDay(selectedStartDateOfWeek, timezone),
                  {
                    representation: 'complete',
                  },
                );
                const endDate = formatISO(startOfTZDay(addedDate, timezone), {
                  representation: 'complete',
                });

                // Only include sequence_day and sequence_prev_dt if sequenceNumber is not 0
                let sequenceDay: number | undefined;
                let sequencePrevDt: string | undefined;

                if (sequenceNumber !== 0) {
                  sequenceDay = sequenceNumber;
                  const prevSequenceNumber = sequenceNumber - 1;
                  const prevDay = sequenceDays?.find(
                    (day) => day.sequenceNumber === prevSequenceNumber,
                  );
                  const startTimeOfPrevDay =
                    prevDay?.selectedTimeSlot?.start_time;

                  // sequencePrevDt is the start time of the previous day with timezone
                  sequencePrevDt =
                    prevDay?.selectedDate && startTimeOfPrevDay
                      ? formatISO(
                          createTZDate(
                            `${prevDay.selectedDate}T${startTimeOfPrevDay}:00`,
                            timezone,
                          ),
                          {
                            representation: 'complete',
                          },
                        )
                      : undefined;
                }

                return {
                  templateId,
                  timezone,
                  eventId: (eventDetails as IEvent)?.id,
                  sourceId: session?.source_id,
                  startDate,
                  endDate,
                  sequenceDay,
                  sequencePrevDt,
                };
              } catch (error) {
                captureSentryMessage('Error setting availability api params', {
                  level: 'error',
                  tags: {product: 'scheduler'},
                  extra: {
                    error,
                    timezone,
                    eventDetails,
                    eventTemplate,
                    session,
                    availabilityRange,
                    sequenceDays,
                    currentSequenceNumber,
                  },
                });
                throw error;
              }
            },
            onDone: {
              target: 'availabilityLoaded',
              actions: assign({
                sequenceDays: ({context, event}) => {
                  const {
                    sequenceDays,
                    currentSequenceNumber,
                    eventTemplate,
                    timezone,
                  } = context;
                  const firstAvailableDate = Object.keys(
                    event.output?.free_time_slots || {},
                  )[0];
                  const sequenceNumber = currentSequenceNumber ?? 0;

                  if (sequenceNumber === 0) {
                    const dayZeroSequenceDay = {
                      ...(sequenceDays?.[0] ?? {}),
                      selectedDate: firstAvailableDate,
                      timeSlots: {
                        free_time_slots: {
                          ...sequenceDays?.[0].timeSlots?.free_time_slots,
                          ...event.output?.free_time_slots,
                        },
                      },
                    };
                    return generateSequenceDaysFromDayZeroDateChange({
                      dayZeroSequenceDay: dayZeroSequenceDay as SequenceDay,
                      sequence: eventTemplate?.metadata?.sequence,
                      timezone,
                    });
                  }
                  // else update the selected date and time slot for the current day
                  return updateSequenceDays(sequenceDays, sequenceNumber, {
                    ...(sequenceDays?.[sequenceNumber] ?? {}),
                    selectedDate: firstAvailableDate,
                    timeSlots: event.output,
                  }) as SequenceDay[];
                },
              }),
            },
            onError: {
              target: 'availabilityError',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'getAvailability',
          },
        },

        availabilityLoaded: {
          always: [
            {
              target: 'availabilitySlots',
              guard: {
                type: 'isSlotsAvailableForCurrentWeek',
              },
            },
            {
              target: 'availabilityNotify',
              reenter: true,
              guard: 'isNoSlotsAvailableTillLastWeek',
            },
            {
              target: 'availabilityEmpty',
              reenter: true,
            },
          ],
        },

        availabilityError: {
          on: {
            retry: {
              target: 'availabilityLoading',
            },
          },
        },

        availabilitySlots: {
          on: {
            'timeslot.confirm': {
              target: 'timeslotConfirmation',
              actions: assign({
                sequenceDays: ({context, event}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: event.timeSlot,
                    },
                  ) as SequenceDay[],
              }),
            },
          },
        },

        availabilityEmpty: {},

        timeslotConfirmation: {
          on: {
            'timeslot.confirm': {
              target: 'timeslotConfirmation',
              actions: assign({
                sequenceDays: ({context, event}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: event.timeSlot,
                    },
                  ) as SequenceDay[],
              }),
            },

            'bookingForm.go': {
              target: '#booking.bookingForm',
              guard: 'isNewBooking',
            },

            'rescheduleReasonForm.go': {
              target: '#booking.rescheduleForm',
            },

            back: {
              target: 'availabilitySlots',
              actions: assign({
                sequenceDays: ({context}): SequenceDay[] => {
                  // Always clear Day 1's time slot (sequenceNumber 0)
                  return updateSequenceDays(context.sequenceDays, 0, {
                    selectedTimeSlot: undefined,
                  }) as SequenceDay[];
                },
                // If on Day 2 or later, go back to Day 1 (sequenceNumber 0)
                currentSequenceNumber: ({context}) => {
                  const sequenceNumber = context.currentSequenceNumber ?? 0;
                  return sequenceNumber > 0 ? 0 : sequenceNumber;
                },
              }),
            },

            'requestMoreAvailabilityForm.go':
              '#booking.requestMoreAvailabilityForm',
          },
        },

        availabilityNotify: {
          on: {
            'notifyHost.go': {
              target: '#booking.notify',
              reenter: true,
            },
            'day.prev': {
              target: 'availabilityLoading',
              actions: assign({
                currentSequenceNumber: ({context}) =>
                  (context.currentSequenceNumber ?? 0) - 1,
              }),
            },
          },
        },
      },
    },

    availabilityRangeError: {
      on: {
        retry: {
          target: 'availabilityRangeLoading',
        },
      },
    },

    bookingSuccess: {
      type: 'final',
      entry: assign({
        finalState: 'BookingSuccess',
      }),
    },

    notifySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'NotifySuccess',
      }),
    },

    notify: {
      initial: 'form',
      on: {
        back: {
          target: '#booking.dateTimePicker.availabilityLoading',
          actions: assign({
            currentSequenceNumber: ({context}) =>
              (context.currentSequenceNumber ?? 0) - 1,
          }),
        },
      },
      states: {
        form: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },

        submitting: {
          invoke: {
            input: ({
              context: {
                session,
                eventTemplate,
                eventDetails,
                timezone,
                currentSequenceNumber,
              },
              event,
            }) => {
              assertEvent(event, 'notify.submit');
              const templateId =
                eventTemplate?.id ||
                (eventDetails as IEvent)?.scheduled_from_template_id;
              if (!templateId) {
                throw new Error('Template id not found');
              }
              return {
                templateId,
                guestName: event.guestName,
                guestEmail: event.guestEmail,
                guestPhone: event.guestPhone,
                sessionId: session?.id,
                guestTimezone: timezone,
                sequenceDay: currentSequenceNumber ?? 0,
              };
            },
            onDone: {
              target: '#booking.notifySuccess',
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'sendNotification',
          },
        },

        error: {
          on: {
            'notify.submit': {
              target: 'submitting',
            },
          },
        },
      },
    },

    bookingForm: {
      initial: 'form',
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {
                eventTemplate,
                timezone,
                sequenceDays,
                session,
                language,
              },
              event,
            }) => {
              assertEvent(event, 'form.submit');

              // Get date and start time from day 0
              const {selectedDate, selectedTimeSlot, timeSlots} =
                sequenceDays?.[0] || {};
              const date = selectedDate;
              const startTime = selectedTimeSlot?.start_time;

              if (!timeSlots) {
                throw new Error('Time slots not found for current day');
              }

              // get formatted date and start time from all days
              const selectedTimeslots = getSelectedTimeslots(sequenceDays);

              const firstAvailableDate = Object.keys(
                timeSlots.free_time_slots || {},
              )[0];
              const firstAvailableTimeList =
                timeSlots.free_time_slots[firstAvailableDate];
              const startAvailableTime = firstAvailableTimeList?.[0].start_time;

              if (eventTemplate?.id && date && startTime) {
                return {
                  templateId: eventTemplate?.id,
                  timezone,
                  date,
                  startTime,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  firstAvailableDatetime: `${firstAvailableDate} ${startAvailableTime}`,
                  sessionId: session?.id,
                  language,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                  selectedTimeslots,
                };
              }
              throw new Error(
                'Template id, selected date or selected time slot not found',
              );
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'createEvent',
          },
        },
        error: {
          always: [
            {
              target: 'unavailableTimeSlot',
              guard: 'isTimeSlotUnavailable',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
        unavailableTimeSlot: {
          on: {
            'refetchAvailability.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },
      },
      on: {
        'bookingForm.back': {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
    },

    requestMoreAvailabilitySuccess: {
      type: 'final',
      entry: assign({
        finalState: 'RequestMoreAvailabilitySuccess',
      }),
    },

    requestMoreAvailabilityForm: {
      initial: 'form',
      states: {
        form: {
          on: {
            'form.submit': {
              target: 'submitting',
            },
          },
        },
        submitting: {
          invoke: {
            input: ({
              context: {eventTemplate, timezone, session, language},
              event,
            }) => {
              assertEvent(event, 'form.submit');

              if (eventTemplate?.id) {
                return {
                  templateId: eventTemplate?.id,
                  guestName: event.guestName,
                  guestEmail: event.guestEmail,
                  guestPhone: event.guestPhone,
                  timezone,
                  language,
                  sessionId: session?.id,
                  source: session?.source,
                  additionalQuestionAnswers:
                    event.additionalQuestionAnswers || [],
                };
              }
              throw new Error(
                'Template id, selected date or selected time slot not found',
              );
            },
            onDone: {
              target: '#booking.requestMoreAvailabilitySuccess',
              reenter: true,
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'requestMoreAvailability',
          },
        },
        error: {
          always: [
            {
              target: '#booking.slotRequestAlreadyExists',
              guard: 'isSlotRequestAlreadyExists',
              reenter: true,
            },
            'retrySubmit',
          ],
        },
        retrySubmit: {
          on: {
            'form.submit': {
              target: 'submitting',
              reenter: true,
            },
          },
        },
      },
      on: {
        'requestMoreAvailabilityForm.back': {
          target: '#booking.dateTimePicker.availabilitySlots',
        },
      },
    },

    slotRequestAlreadyExists: {
      on: {
        'refetchAvailability.go': {
          target: '#booking.dateTimePicker.availabilityLoading',
          reenter: true,
        },
      },
    },

    rescheduleForm: {
      initial: 'rescheduleReasonForm',
      on: {
        'rescheduleReasonForm.back': {
          target: 'dateTimePicker.availabilitySlots',
          reenter: true,
          actions: assign({
            rescheduleReason: undefined,
          }),
        },
      },
      states: {
        rescheduleReasonForm: {
          on: {
            'rescheduleReason.update': {
              actions: assign({
                rescheduleReason: ({event}) => event.reason,
              }),
            },
            'timeslot.confirm': 'rescheduleFormSubmitting',
          },
        },

        rescheduleFormSubmitting: {
          invoke: {
            input: ({
              context: {
                eventDetails,
                attendee,
                sequenceDays,
                currentSequenceNumber,
                timezone,
                rescheduleReason,
              },
              self,
            }) => {
              const sequenceNumber = currentSequenceNumber ?? 0;
              const selectedDay = sequenceDays?.find(
                (day) => day.sequenceNumber === sequenceNumber,
              );
              const timeSlots = selectedDay?.timeSlots;

              if (!timeSlots) {
                throw new Error('Time slots not found for current day');
              }

              const firstDate = Object.keys(timeSlots.free_time_slots || {})[0];
              const firstDateTimeList = timeSlots.free_time_slots[firstDate];
              const startTime = firstDateTimeList?.[0].start_time;

              const rootMachine = self.system.get('root');
              const rootSnapshot = rootMachine?.getSnapshot();

              // Get values from root context
              const aid = rootSnapshot?.context.aid;

              if ((eventDetails as IEvent)?.id && selectedDay?.selectedDate) {
                return {
                  eventId: (eventDetails as IEvent)?.id,
                  date: selectedDay.selectedDate,
                  startTime: selectedDay.selectedTimeSlot?.start_time || '',
                  timezone: timezone || '',
                  attendee: attendee || '',
                  firstAvailableDatetime: `${firstDate} ${startTime}`,
                  reason: rescheduleReason || '',
                  aid:
                    (eventDetails as IEvent)?.candidates?.[0]?.id || aid || '',
                };
              }
              throw new Error('Event id or selected date not found');
            },
            onDone: {
              target: '#booking.bookingSuccess',
              actions: assign({
                eventDetails: ({event}) => event.output,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({event}) => event.error,
              }),
            },
            src: 'updateEvent',
          },
        },

        error: {
          always: [
            {
              target: 'unavailableTimeSlotForReschedule',
              guard: 'isTimeSlotUnavailable',
            },
            {
              target: 'retryRescheduleFormSubmit',

              actions: assign({
                sequenceDays: ({context}): SequenceDay[] =>
                  updateSequenceDays(
                    context.sequenceDays,
                    context.currentSequenceNumber ?? 0,
                    {
                      selectedTimeSlot: undefined,
                    },
                  ) as SequenceDay[],
              }),

              reenter: true,
            },
          ],
        },

        unavailableTimeSlotForReschedule: {
          on: {
            'refetchAvailabilityForReschedule.go': {
              target: '#booking.dateTimePicker.availabilityLoading',
              reenter: true,
            },
          },
        },

        retryRescheduleFormSubmit: {
          on: {
            retryRescheduleFormSubmitting: 'rescheduleFormSubmitting',
          },
        },
      },
    },
  },
  output: ({context: {eventDetails, finalState, rescheduleReason}}) => ({
    eventDetails,
    finalState,
    rescheduleReason,
  }),
});

export type IBookingMachineSnapshot = SnapshotFrom<typeof bookingMachine>;
